import React, { useState, useEffect } from 'react';
import ReserveSettings from './ReserveSettings';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star,
  CreditCard,
  Smartphone,
  Banknote,
  ArrowLeftRight,
  Check,
  X,
  AlertCircle,
  Settings,
  ToggleLeft,
  ToggleRight,
  RefreshCw
} from 'lucide-react';

const PAYMENT_SETTINGS_CACHE_KEY = "moo_payment_settings_cache";

const parseBooleanFlag = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0") return false;
  }
  return fallback;
};

const PaymentMethodsManagement = () => {
  const syncPaymentSettingsState = (
    multiEnabled,
    creditEnabled,
    aggregatorsEnabled = false,
    yangoEnabled = false,
    glovoEnabled = false,
  ) => {
    const payload = {
      multiPaymentEnabled: parseBooleanFlag(multiEnabled, false),
      creditSaleEnabled: parseBooleanFlag(creditEnabled, false),
      aggregatorsEnabled: parseBooleanFlag(aggregatorsEnabled, false),
      aggregatorYangoEnabled: parseBooleanFlag(yangoEnabled, false),
      aggregatorGlovoEnabled: parseBooleanFlag(glovoEnabled, false),
      updatedAt: Date.now(),
    };
    try {
      localStorage.setItem(PAYMENT_SETTINGS_CACHE_KEY, JSON.stringify(payload));
    } catch (_error) {
      // Ignorer les erreurs de localStorage
    }
    window.dispatchEvent(
      new CustomEvent("moo-payment-settings-updated", {
        detail: {
          multiPaymentEnabled: payload.multiPaymentEnabled,
          creditSaleEnabled: payload.creditSaleEnabled,
          aggregatorsEnabled: payload.aggregatorsEnabled,
          aggregatorYangoEnabled: payload.aggregatorYangoEnabled,
          aggregatorGlovoEnabled: payload.aggregatorGlovoEnabled,
        },
      }),
    );
  };

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [multiPaymentEnabled, setMultiPaymentEnabled] = useState(false);
  const [creditSaleEnabled, setCreditSaleEnabled] = useState(false);
  const [aggregatorsEnabled, setAggregatorsEnabled] = useState(false);
  const [aggregatorYangoEnabled, setAggregatorYangoEnabled] = useState(false);
  const [aggregatorGlovoEnabled, setAggregatorGlovoEnabled] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    icon_name: 'banknote',
    color: '#3B82F6',
    description: '',
    is_active: true,
    is_default: false
  });

  const iconOptions = [
    { name: 'banknote', icon: Banknote, label: 'Espèces' },
    { name: 'credit-card', icon: CreditCard, label: 'Carte' },
    { name: 'smartphone', icon: Smartphone, label: 'Mobile' },
    { name: 'arrow-left-right', icon: ArrowLeftRight, label: 'Virement' }
  ];

  const colorOptions = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#F97316', // Orange
    '#06B6D4', // Cyan
    '#84CC16'  // Lime
  ];

  useEffect(() => {
    loadPaymentMethods();
    loadPaymentSettings();
  }, []);

  // Fonction pour afficher les notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadPaymentMethods = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
      setLoading(true);
      }
      
      console.log('🔄 Chargement des moyens de paiement admin...');
      const response = await fetch('/api/payment-methods/all');
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Moyens de paiement reçus:', data);
        setPaymentMethods(data);
        
        if (showRefreshIndicator) {
          showNotification('Liste des moyens de paiement mise à jour', 'success');
        }
      } else {
        console.error('❌ Erreur lors du chargement des moyens de paiement:', response.status);
        showNotification('Erreur lors du chargement des moyens de paiement', 'error');
      }
    } catch (error) {
      console.error('❌ Erreur API moyens de paiement:', error);
      showNotification('Erreur de connexion lors du chargement', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      const ts = Date.now();
      const [multiRes, creditRes, aggregatorsRes, yangoRes, glovoRes] = await Promise.all([
        fetch(`/api/settings/multi-payment?_=${ts}`, { cache: "no-store" }),
        fetch(`/api/settings/credit-sale?_=${ts}`, { cache: "no-store" }),
        fetch(`/api/settings/aggregators?_=${ts}`, { cache: "no-store" }),
        fetch(`/api/settings/aggregator-yango?_=${ts}`, { cache: "no-store" }),
        fetch(`/api/settings/aggregator-glovo?_=${ts}`, { cache: "no-store" }),
      ]);

      let nextMulti = false;
      let nextCredit = false;
      let nextAggregators = false;
      let nextYango = false;
      let nextGlovo = false;

      if (multiRes.ok) {
        const data = await multiRes.json();
        nextMulti = parseBooleanFlag(data?.enabled, false);
        setMultiPaymentEnabled(nextMulti);
      } else {
        console.error('Erreur lors du chargement des paramètres multi-moyens');
      }

      if (creditRes.ok) {
        const data = await creditRes.json();
        nextCredit = parseBooleanFlag(data?.enabled, false);
        setCreditSaleEnabled(nextCredit);
      } else {
        console.error('Erreur lors du chargement des paramètres vente à crédit');
      }

      if (aggregatorsRes.ok) {
        const data = await aggregatorsRes.json();
        nextAggregators = parseBooleanFlag(data?.enabled, false);
        setAggregatorsEnabled(nextAggregators);
      } else {
        console.error('Erreur lors du chargement des paramètres agrégateurs');
      }

      if (yangoRes.ok) {
        const data = await yangoRes.json();
        nextYango = parseBooleanFlag(data?.enabled, false);
        setAggregatorYangoEnabled(nextYango);
      } else {
        console.error('Erreur lors du chargement du paramètre Yango');
      }

      if (glovoRes.ok) {
        const data = await glovoRes.json();
        nextGlovo = parseBooleanFlag(data?.enabled, false);
        setAggregatorGlovoEnabled(nextGlovo);
      } else {
        console.error('Erreur lors du chargement du paramètre Glovo');
      }

      syncPaymentSettingsState(nextMulti, nextCredit, nextAggregators, nextYango, nextGlovo);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const toggleMultiPayment = async () => {
    try {
      setLoadingSettings(true);
      const newValue = !multiPaymentEnabled;
      
      const response = await fetch('/api/settings/multi-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: newValue }),
      });

      if (response.ok) {
        setMultiPaymentEnabled(newValue);
        syncPaymentSettingsState(
          newValue,
          creditSaleEnabled,
          aggregatorsEnabled,
          aggregatorYangoEnabled,
          aggregatorGlovoEnabled,
        );
        showNotification(`Paiement multi-moyens ${newValue ? 'activé' : 'désactivé'}`, 'success');
        console.log('✅ Paramètre multi-paiement mis à jour:', newValue);
      } else {
        console.error('Erreur lors de la mise à jour des paramètres');
        showNotification('Erreur lors de la mise à jour des paramètres', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoadingSettings(false);
    }
  };

  const toggleCreditSale = async () => {
    try {
      setLoadingSettings(true);
      const newValue = !creditSaleEnabled;

      const response = await fetch('/api/settings/credit-sale', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: newValue }),
      });

      if (response.ok) {
        setCreditSaleEnabled(newValue);
        syncPaymentSettingsState(
          multiPaymentEnabled,
          newValue,
          aggregatorsEnabled,
          aggregatorYangoEnabled,
          aggregatorGlovoEnabled,
        );
        showNotification(`Vente à crédit ${newValue ? 'activée' : 'désactivée'}`, 'success');
      } else {
        showNotification('Erreur lors de la mise à jour du mode crédit', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoadingSettings(false);
    }
  };

  const toggleAggregatorSetting = async (endpoint, currentValue, applyValue, label) => {
    try {
      setLoadingSettings(true);
      const newValue = !currentValue;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: newValue }),
      });

      if (response.ok) {
        applyValue(newValue);
        const nextAggregators =
          endpoint === '/api/settings/aggregators' ? newValue : aggregatorsEnabled;
        const nextYango =
          endpoint === '/api/settings/aggregator-yango' ? newValue : aggregatorYangoEnabled;
        const nextGlovo =
          endpoint === '/api/settings/aggregator-glovo' ? newValue : aggregatorGlovoEnabled;
        syncPaymentSettingsState(
          multiPaymentEnabled,
          creditSaleEnabled,
          nextAggregators,
          nextYango,
          nextGlovo,
        );
        showNotification(`${label} ${newValue ? 'activé' : 'désactivé'}`, 'success');
      } else {
        showNotification(`Erreur lors de la mise à jour de ${label}`, 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Préparer les données avec l'ID si on modifie
      const dataToSend = editingMethod 
        ? { ...formData, id: editingMethod.id }
        : formData;

      console.log('🔄 Sauvegarde moyen de paiement:', dataToSend);

      const response = await fetch('/api/payment-methods', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Moyen de paiement sauvegardé:', result);
        
        // Recharger les données et fermer le modal
        await loadPaymentMethods(true);
        handleCloseModal();
        
        showNotification(
          editingMethod ? 'Moyen de paiement modifié avec succès' : 'Moyen de paiement ajouté avec succès',
          'success'
        );
      } else {
        const errorData = await response.json();
        console.error('❌ Erreur lors de la sauvegarde:', errorData);
        showNotification('Erreur lors de la sauvegarde: ' + (errorData.error || 'Erreur inconnue'), 'error');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      showNotification('Erreur de connexion lors de la sauvegarde', 'error');
    }
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setFormData({
      name: method.name,
      code: method.code,
      icon_name: method.icon_name,
      color: method.color,
      description: method.description || '',
      is_active: method.is_active,
      is_default: method.is_default
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const method = paymentMethods.find(m => m.id === id);
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le moyen de paiement "${method?.name}" ?`)) {
      try {
        const response = await fetch(`/api/payment-methods/${id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          console.log('✅ Moyen de paiement supprimé:', id);
          await loadPaymentMethods(true);
          showNotification('Moyen de paiement supprimé avec succès', 'success');
        } else {
          const errorData = await response.json();
          console.error('❌ Erreur lors de la suppression:', errorData);
          showNotification('Erreur lors de la suppression: ' + (errorData.error || 'Erreur inconnue'), 'error');
        }
      } catch (error) {
        console.error('❌ Erreur:', error);
        showNotification('Erreur de connexion lors de la suppression', 'error');
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const method = paymentMethods.find(m => m.id === id);
      console.log('🔄 Changement statut moyen de paiement:', id, method?.name);
      
      const response = await fetch(`/api/payment-methods/${id}/toggle`, {
        method: 'PATCH',
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Statut modifié:', result);
        await loadPaymentMethods(true);
        
        const newStatus = !method?.is_active;
        showNotification(
          `Moyen de paiement "${method?.name}" ${newStatus ? 'activé' : 'désactivé'}`,
          'success'
        );
      } else {
        const errorData = await response.json();
        console.error('❌ Erreur lors du changement de statut:', errorData);
        showNotification('Erreur lors du changement de statut: ' + (errorData.error || 'Erreur inconnue'), 'error');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      showNotification('Erreur de connexion lors du changement de statut', 'error');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      const method = paymentMethods.find(m => m.id === id);
      console.log('🔄 Définition moyen par défaut:', id, method?.name);
      
      const response = await fetch(`/api/payment-methods/${id}/default`, {
        method: 'PATCH',
      });

      if (response.ok) {
        console.log('✅ Moyen par défaut défini:', id);
        await loadPaymentMethods(true);
        showNotification(`"${method?.name}" défini comme moyen de paiement par défaut`, 'success');
      } else {
        const errorData = await response.json();
        console.error('❌ Erreur lors de la définition par défaut:', errorData);
        showNotification('Erreur lors de la définition par défaut: ' + (errorData.error || 'Erreur inconnue'), 'error');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      showNotification('Erreur de connexion lors de la définition par défaut', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMethod(null);
    setFormData({
      name: '',
      code: '',
      icon_name: 'banknote',
      color: '#3B82F6',
      description: '',
      is_active: true,
      is_default: false
    });
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(opt => opt.name === iconName);
    return iconOption ? iconOption.icon : Banknote;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-white">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-lg">Chargement des moyens de paiement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      {/* Notifications */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-600 text-white' 
            : 'bg-red-600 text-white'
        }`}>
          <div className="flex items-center space-x-2">
            {notification.type === 'success' ? (
              <Check size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Moyens de Paiement</h1>
            <p className="text-gray-400">Gérez les moyens de paiement disponibles dans votre système</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => loadPaymentMethods(true)}
              disabled={refreshing}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
              Actualiser
            </button>
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            Ajouter un moyen
          </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-6 lg:grid-cols-2">
          {/* Paramètres de Paiement Multi-Moyens */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <Settings size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Paiement Multi-Moyens</h3>
                  <p className="text-gray-400 text-sm">
                    Permettre aux clients de payer avec plusieurs moyens de paiement
                  </p>
                </div>
              </div>
              <button
                onClick={toggleMultiPayment}
                disabled={loadingSettings}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  multiPaymentEnabled 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loadingSettings ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  multiPaymentEnabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />
                )}
                {multiPaymentEnabled ? 'Activé' : 'Désactivé'}
              </button>
            </div>
            
            {multiPaymentEnabled && (
              <div className="mt-4 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-200">
                    <p className="font-medium mb-2">Règles du paiement multi-moyens :</p>
                    <ul className="space-y-1 text-blue-300">
                      <li>• Seul le moyen "Espèce" peut générer de la monnaie à rendre</li>
                      <li>• Le total des moyens non-espèces ne peut pas dépasser le montant de la commande</li>
                      <li>• Vous pouvez payer partiellement en espèces et compléter avec d'autres moyens</li>
                      <li>• Le montant total doit couvrir au minimum le montant de la commande</li>
                    </ul>
                    <div className="mt-3 p-3 bg-blue-800/30 rounded border border-blue-400/30">
                      <p className="font-medium text-blue-100 mb-1">Exemple :</p>
                      <p className="text-xs text-blue-200">
                        Commande de 1000 F → Client donne 5000 F en espèces mais veut payer seulement 500 F avec, 
                        puis 500 F en carte. ✅ Autorisé ! Monnaie rendue : 4500 F.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Paramètre Vente à crédit */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-600 rounded-lg">
                  <CreditCard size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Vente à crédit</h3>
                  <p className="text-gray-400 text-sm">
                    Autoriser le mode de paiement “À crédit” sur la caisse
                  </p>
                </div>
              </div>
              <button
                onClick={toggleCreditSale}
                disabled={loadingSettings}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  creditSaleEnabled
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loadingSettings ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  creditSaleEnabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />
                )}
                {creditSaleEnabled ? 'Activé' : 'Désactivé'}
              </button>
            </div>
            {creditSaleEnabled && (
              <div className="mt-4 p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                <p className="text-sm text-purple-200">
                  Le caissier pourra sélectionner <span className="font-semibold">“À crédit”</span> dans le modal de paiement
                  et rattacher la vente à un créancier du répertoire.
                </p>
              </div>
            )}
          </div>

          {/* Paramètre Vente via Agrégateur */}
          <ReserveSettings />
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 lg:col-span-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500 rounded-lg">
                  <Smartphone size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Vente via Agrégateur</h3>
                  <p className="text-gray-400 text-sm">
                    Activer les commandes provenant de plateformes comme Yango ou Glovo
                  </p>
                </div>
              </div>
              <button
                onClick={() =>
                  toggleAggregatorSetting(
                    '/api/settings/aggregators',
                    aggregatorsEnabled,
                    setAggregatorsEnabled,
                    'Vente via agrégateur',
                  )
                }
                disabled={loadingSettings}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  aggregatorsEnabled
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loadingSettings ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  aggregatorsEnabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />
                )}
                {aggregatorsEnabled ? 'Activé' : 'Désactivé'}
              </button>
            </div>

            {aggregatorsEnabled && (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() =>
                    toggleAggregatorSetting(
                      '/api/settings/aggregator-yango',
                      aggregatorYangoEnabled,
                      setAggregatorYangoEnabled,
                      'Yango',
                    )
                  }
                  disabled={loadingSettings}
                  className={`flex items-center justify-between rounded-lg border p-4 transition-all ${
                    aggregatorYangoEnabled
                      ? 'border-red-400 bg-red-500/10 text-white'
                      : 'border-gray-600 bg-gray-700/40 text-gray-300 hover:bg-gray-700'
                  } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className="flex items-center gap-3">
                    <img src="/icons/yango.png" alt="Yango" className="h-8 w-8 rounded-md object-contain" />
                    <span className="font-semibold">Yango</span>
                  </span>
                  {aggregatorYangoEnabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    toggleAggregatorSetting(
                      '/api/settings/aggregator-glovo',
                      aggregatorGlovoEnabled,
                      setAggregatorGlovoEnabled,
                      'Glovo',
                    )
                  }
                  disabled={loadingSettings}
                  className={`flex items-center justify-between rounded-lg border p-4 transition-all ${
                    aggregatorGlovoEnabled
                      ? 'border-yellow-400 bg-yellow-500/10 text-white'
                      : 'border-gray-600 bg-gray-700/40 text-gray-300 hover:bg-gray-700'
                  } ${loadingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className="flex items-center gap-3">
                    <img src="/icons/glovo-moo-sync.png" alt="Glovo" className="h-8 w-8 rounded-md object-contain" />
                    <span className="font-semibold">Glovo</span>
                  </span>
                  {aggregatorGlovoEnabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Payment Methods List */}
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          {refreshing && (
            <div className="p-4 bg-blue-900/20 border-b border-gray-700">
              <div className="flex items-center justify-center space-x-2 text-blue-400">
                <RefreshCw size={16} className="animate-spin" />
                <span className="text-sm">Mise à jour en cours...</span>
              </div>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Moyen de Paiement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Par défaut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {paymentMethods.map((method) => {
                  const IconComponent = getIconComponent(method.icon_name);
                  return (
                    <tr key={method.id} className="hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
                            style={{ backgroundColor: method.color }}
                          >
                            <IconComponent size={20} className="text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">{method.name}</div>
                            {method.description && (
                              <div className="text-sm text-gray-400">{method.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-mono bg-gray-700 text-gray-300 rounded">
                          {method.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleStatus(method.id)}
                          className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium transition-all hover:scale-105 ${
                            method.is_active 
                              ? 'bg-green-900 text-green-300 hover:bg-green-800' 
                              : 'bg-red-900 text-red-300 hover:bg-red-800'
                          }`}
                        >
                          {method.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                          {method.is_active ? 'Actif' : 'Inactif'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {method.is_default ? (
                          <span className="flex items-center gap-2 text-yellow-400">
                            <Star size={16} />
                            Par défaut
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSetDefault(method.id)}
                            className="text-gray-400 hover:text-yellow-400 flex items-center gap-2 transition-colors"
                          >
                            <Star size={16} />
                            Définir
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(method)}
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(method.id)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {paymentMethods.length === 0 && (
              <div className="p-8 text-center text-gray-400">
                <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">Aucun moyen de paiement configuré</p>
                <p className="text-sm">Cliquez sur "Ajouter un moyen" pour commencer</p>
              </div>
            )}
          </div>
          </div>
        </div>

      {/* Modal d'ajout/modification */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">
                  {editingMethod ? 'Modifier le moyen de paiement' : 'Ajouter un moyen de paiement'}
                </h2>
                <button
                  onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Code
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 font-mono"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Icône
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {iconOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <button
                          key={option.name}
                          type="button"
                          onClick={() => setFormData({ ...formData, icon_name: option.name })}
                        className={`p-3 rounded-lg border-2 flex items-center justify-center transition-all ${
                            formData.icon_name === option.name
                              ? 'border-blue-500 bg-blue-600'
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <IconComponent size={20} className="text-white" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Couleur
                  </label>
                  <div className="grid grid-cols-8 gap-2">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        formData.color === color ? 'border-white scale-110' : 'border-gray-600'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description (optionnel)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    rows="2"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-gray-300">Actif</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-gray-300">Par défaut</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Check size={16} />
                    {editingMethod ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
    </div>
  );
};

export default PaymentMethodsManagement; 
